/**
 * LivenessHandoffPage
 * ====================
 * Standalone page opened via QR code on phone.
 * Validates handoff token, runs liveness challenges, reports completion.
 * No SDK auth required - uses the handoff token for authentication.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import MediaPipeLivenessStep from './steps/MediaPipeLivenessStep';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function LivenessHandoffPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('validating'); // validating, ready, completed, expired, error
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);

  // Validate the handoff token on mount
  useEffect(() => {
    async function validate() {
      try {
        const res = await fetch(`${API_BASE}/api/v1/sdk/liveness/handoff/${token}`);
        if (!res.ok) {
          setStatus('expired');
          return;
        }
        const data = await res.json();
        if (!data.valid) {
          setStatus('expired');
          return;
        }
        setSessionId(data.session_id);
        setStatus('ready');
      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    }
    validate();
  }, [token]);

  // Create a minimal client that uses the handoff token for auth
  const handoffClient = {
    createLivenessSession: async () => {
      // Session already exists from handoff - just return it
      return { session_id: sessionId };
    },
    completeLiveness: async (_sessionId, confidence, { challengesCompleted } = {}) => {
      const res = await fetch(`${API_BASE}/api/v1/sdk/liveness/handoff/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challenges_completed: challengesCompleted,
          confidence,
        }),
      });
      if (!res.ok) throw new Error('Failed to complete');
      return res.json();
    },
  };

  const handleComplete = useCallback(() => {
    setStatus('completed');
  }, []);

  // === VALIDATING ===
  if (status === 'validating') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <Loader2 size={32} style={{ color: 'var(--primary)', animation: 'spin 1s linear infinite' }} />
          <p style={styles.text}>Verifying session...</p>
        </div>
      </div>
    );
  }

  // === EXPIRED ===
  if (status === 'expired') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <AlertCircle size={40} color="#EF4444" />
          <h3 style={styles.title}>Session Expired</h3>
          <p style={styles.text}>
            This liveness link has expired or has already been used.
            Please scan a new QR code from your desktop.
          </p>
        </div>
      </div>
    );
  }

  // === ERROR ===
  if (status === 'error') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <AlertCircle size={40} color="#EF4444" />
          <h3 style={styles.title}>Something went wrong</h3>
          <p style={styles.text}>{error || 'Unable to verify this session.'}</p>
        </div>
      </div>
    );
  }

  // === COMPLETED ===
  if (status === 'completed') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.successIcon}>
            <CheckCircle size={48} color="#10B981" />
          </div>
          <h3 style={styles.title}>Liveness Verified!</h3>
          <p style={styles.text}>
            You can now close this page and return to your desktop.
            The verification will continue automatically.
          </p>
        </div>
      </div>
    );
  }

  // === READY - Show liveness challenges ===
  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <Shield size={20} style={{ color: 'var(--primary)' }} />
        <span style={styles.headerText}>Identity Verification</span>
      </div>
      <MediaPipeLivenessStep
        onComplete={handleComplete}
        client={handoffClient}
      />
    </div>
  );
}

const styles = {
  page: {
    '--primary': '#0D9488',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F9FAFB',
    padding: '16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '16px',
    position: 'fixed',
    top: '16px',
    zIndex: 10,
  },
  headerText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1F2937',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '40px 24px',
    background: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    maxWidth: '360px',
    width: '100%',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#D1FAE5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1F2937',
    margin: '12px 0 8px',
  },
  text: {
    fontSize: '14px',
    color: '#6B7280',
    lineHeight: '1.5',
    margin: '8px 0 0',
  },
};
